import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { BASE_URL } from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get('oobCode');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(Boolean(oobCode));
  const [invalidCode, setInvalidCode] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  useEffect(() => {
    if (!oobCode) {
      setVerifying(false);
      return;
    }

    let isMounted = true;

    verifyPasswordResetCode(auth, oobCode)
      .then((email) => {
        if (isMounted) setResetEmail(email);
      })
      .catch((err) => {
        console.error('Reset link verification error:', err);
        if (isMounted) {
          setInvalidCode(true);
          setError('This reset link is invalid or has expired. Please request a new one.');
        }
      })
      .finally(() => {
        if (isMounted) setVerifying(false);
      });

    return () => {
      isMounted = false;
    };
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);

      // The reset changes the Firebase password but leaves the temp-password flags on the
      // user doc, which would reject the next login as "expired". Clear them via the same
      // endpoint the forced-change flow uses. Best-effort: the password itself is already
      // reset, so a failure here must not be reported as a reset failure.
      if (resetEmail) {
        try {
          const credential = await signInWithEmailAndPassword(auth, resetEmail, newPassword);
          const idToken = await credential.user.getIdToken(true);
          const response = await fetch(`${BASE_URL}/auth/clear-password-reset`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({}),
          });
          if (!response.ok) console.warn('Failed to clear password reset flag:', response.status);
        } catch (clearErr) {
          console.warn('Error clearing password reset flag:', clearErr);
        } finally {
          await signOut(auth).catch(() => {});
        }
      }

      setSuccess(true);
    } catch (err) {
      console.error('Password reset error:', err);
      if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used. Please request a new one.');
      } else if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const wrap = (title, children) => (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900">{title}</h2>
        {children}
      </div>
    </div>
  );

  if (!oobCode || invalidCode) {
    return wrap('Invalid Reset Link', (
      <>
        <p className="text-sm text-center text-red-600 font-medium">
          {error || 'This reset link is invalid. Please request a new password reset.'}
        </p>
        <div className="text-center">
          <Link to="/forgot-password" className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark">
            Request a new reset link
          </Link>
        </div>
      </>
    ));
  }

  if (verifying) {
    return wrap('Validating Reset Link', (
      <p className="text-sm text-center text-gray-600">Please wait while we verify your reset link.</p>
    ));
  }

  if (success) {
    return wrap('Password Reset', (
      <>
        <p className="text-sm text-center text-green-600 font-medium">
          Your password has been reset successfully.
        </p>
        <div className="text-center">
          <Link to="/login" className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark">
            Sign in with your new password
          </Link>
        </div>
      </>
    ));
  }

  return wrap('Set New Password', (
    <>
      {error && <p className="text-sm text-center text-red-600 font-medium">{error}</p>}

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
            New password
          </label>
          <input
            id="newPassword"
            name="newPassword"
            type="password"
            autoComplete="new-password"
            required
            className="block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter new password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
            Confirm password
          </label>
          <input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            autoComplete="new-password"
            required
            className="block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="flex justify-center items-center w-full px-4 py-2 text-sm font-medium text-white bg-jims-cyan-dark border border-transparent rounded-md shadow-sm hover:bg-jims-cyan focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Resetting...
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </div>
      </form>

      <div className="text-center mt-4">
        <Link to="/login" className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark">
          &larr; Back to login
        </Link>
      </div>
    </>
  ));
};

export default ResetPasswordPage;
